import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { get, post } from "../../../components/Utils/CallApi";
import { setPageLoadingTrue, setPageLoadingFalse } from "../appSlice";
import { setError } from "../appSlice";

export const fetchCategories = createAsyncThunk(
  "categories/fetchCategories",
  async ({ companyId, sectionId }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const params = `companyId=${companyId}&sectionId=${sectionId}`;
      const response = await get("sections/categories/list", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.result || [];
    } catch (error) {
      dispatch(setError(error.message || "Failed to fetch categories"));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const editCategory = createAsyncThunk(
  "categories/editCategory",
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const params = `id=${id}`;
      const response = await get("sections/categories/get", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.result || [];
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const listCategories = createAsyncThunk(
  "categories/listCategories",
  async ({ companyId, sectionId }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const params = `companyId=${companyId}&sectionId=${sectionId}`;
      const response = await get("sections/categories/list", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.result || [];
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const listDistricts = createAsyncThunk(
  "lookups/listDistrict",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const params = "";
      const response = await get("lookups/listDistrict", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.result || [];
    } catch (error) {
      dispatch(setError("An error has occurred"));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const saveCategory = createAsyncThunk(
  "categories/saveCategory",
  async (
    { categoryData, sectionId },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();
      const response = await post(
        "sections/categories/save",
        categoryData,
        state.app,
        sectionId
      );

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.data;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const deleteCategory = createAsyncThunk(
  "categories/deleteCategory",
  async ({ id }, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();

      // Wrap id in an object
      const response = await post(
        "sections/categories/delete",
        { id },
        state.app
      );

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

const categoriesSlice = createSlice({
  name: "categories",
  initialState: {
    categoriesContent: [],
    districts: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categoriesContent = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(listCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categoriesContent = action.payload;
      })
      .addCase(listDistricts.fulfilled, (state, action) => {
        state.loading = false;
        state.districts = action.payload;
      })
      .addCase(listCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(saveCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categoriesContent.push(action.payload);
      })
      .addCase(saveCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default categoriesSlice.reducer;
